import {
  Word,
  fieldsByWhatTheyHold,
  type WordOption,
} from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewWidget.tools";
import { ReportsViewWidgetWhere } from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewWidgetWhere";
import { Select } from "@/components/molecules/Select";

import {
  DRAWINGS,
  PRESENTATION_LABELS,
  SUMMARY_OPERATION_LABELS,
  adaptQuestion,
  axisFieldsFor,
  canBeNarrowed,
  measurableFields,
  questionFromSpec,
  specForQuestion,
  whyItCannotDraw,
  type PresentationKind,
  type ReportSource,
  type SummaryOperation,
  type WidgetQuestion,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";

/**
 * What a widget is asking, written where its title would be.
 *
 * It reads as a sentence - "total amount of transactions by category, as a bar
 * chart" - and every noun in it is live: underlined, lit under the pointer,
 * and one click from a short menu. There is nothing to open and no mode to
 * leave, because a mode you have to find is a mode nobody finds. The sentence
 * is also the card's name, so nothing has to be named before it can be read.
 */
export function ReportsViewWidgetQuestion({
  source,
  sources,
  spec,
  presentation,
  onChange,
}: {
  source: ReportSource | undefined;
  /** Everything the ledger can be asked about, for changing the subject. */
  sources: readonly ReportSource[];
  spec: Record<string, unknown> | undefined;
  presentation: PresentationKind | undefined;
  onChange: (patch: {
    spec?: Record<string, unknown>;
    presentation?: PresentationKind;
    source?: string;
  }) => void;
}) {
  if (!source) return null;

  const question = questionFromSpec(spec);
  const isAsked = source.isSummarized;
  const cuts = axisFieldsFor(source, presentation);
  const measures = measurableFields(source);
  const ways = DRAWINGS.filter((kind) => source.presentations.includes(kind));
  const ask = (next: WidgetQuestion) =>
    onChange({ spec: specForQuestion(spec, next, source) });

  const subject = sources.map((entry) => ({
    value: entry.name,
    label: entry.label,
  }));

  return (
    <div
      // The card header is its drag handle, and the sentence sits in it. Only
      // the words keep their own presses (see ``Word``); the spaces between
      // them, and the room left over at the end of the sentence, belong to
      // the header - which is most of what there is to take hold of, and
      // used to swallow the press and leave the browser selecting text.
      data-question
      // Laid out as text rather than as a row of boxes, so the spaces and the
      // punctuation fall where a reader expects them and a long sentence
      // wraps like a sentence.
      className="min-w-0 [line-height:2] text-muted-foreground/80 text-sm"
    >
      {isAsked && (
        <>
          <Word
            value={question.operation}
            options={(
              Object.keys(SUMMARY_OPERATION_LABELS) as SummaryOperation[]
            )
              .filter(
                (operation) => operation === "count" || measures.length > 0,
              )
              .map((operation) => ({
                value: operation,
                label: SUMMARY_OPERATION_LABELS[operation],
              }))}
            isMidSentence={false}
            onChange={(operation) =>
              ask({
                ...question,
                operation: operation as SummaryOperation,
                field:
                  operation === "count"
                    ? undefined
                    : (question.field ?? measures[0]?.name),
              })
            }
          />{" "}
          {/* "How many transactions", but "Total amount of transactions":
              counting takes the rows themselves, and there is no column to
              name and no "of" to say. */}
          {question.operation !== "count" && (
            <>
              <Word
                value={question.field ?? measures[0]?.name ?? ""}
                options={fieldsByWhatTheyHold(measures, source)}
                onChange={(field) => ask({ ...question, field })}
              />{" "}
              of{" "}
            </>
          )}
        </>
      )}

      <Word
        value={source.name}
        options={subject}
        onChange={(name) => onChange({ source: name })}
        // A source that is asked nothing opens the sentence, and a sentence
        // does not open in lower case.
        isMidSentence={isAsked}
      />

      {isAsked && (
        <>
          {" "}
          per{" "}
          <Word
            value={question.groupBy[0] ?? ""}
            options={fieldsByWhatTheyHold(cuts, source)}
            onChange={(name) => ask({ ...question, groupBy: [name] })}
          />
        </>
      )}

      {/* Which rows the question is asked of, between what it asks and how it
          is drawn: "… per contact, where merchant is Shell, as a bar chart".
          The comma is written here rather than inside the clause, so it stays
          against the word before it when the sentence wraps - carried along,
          it started the next line on its own. */}
      {canBeNarrowed(source) && (
        <>
          ,{" "}
          <ReportsViewWidgetWhere
            source={source}
            where={question.where}
            onChange={(where) => ask({ ...question, where })}
          />
        </>
      )}

      {ways.length > 1 && (
        <>
          , as a{" "}
          <Word
            value={presentation ?? ways[0]}
            options={ways.map((kind) => ({
              value: kind,
              label: PRESENTATION_LABELS[kind],
              // Nothing is withheld: a drawing that cannot answer the question
              // as it stands says so where the choice is made, rather than
              // after it has been chosen.
              note: whyItCannotDraw(kind, question, source),
            }))}
            onChange={(value) => {
              const drawn = value as PresentationKind;
              const fitted = adaptQuestion(question, drawn, source);
              onChange({
                presentation: drawn,
                // The sentence itself says what changed: it reads the
                // question as it now stands.
                ...(fitted.changed.length
                  ? { spec: specForQuestion(spec, fitted.question, source) }
                  : {}),
              });
            }}
          />
        </>
      )}
    </div>
  );
}
